/**
 * Показ денег. Любая цифра объяснима: рядом видно, расчётная она,
 * когда обновлена и откуда взялась (docs/01-product.md, docs/10-design.md).
 */
import type { ReactNode } from 'react';
import { formatMoney, formatNumber } from '@/lib/format';

/** Сумма моноширинным шрифтом. Тревога передаётся цветом числа, не заливкой. */
export function Money({
  minor,
  currency,
  size = 'md',
  tone = 'normal',
}: {
  minor: number | null | undefined;
  currency: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  tone?: 'normal' | 'muted' | 'warning' | 'danger';
}) {
  const fontSize = { sm: 13, md: 15, lg: 20, xl: 30 }[size];
  const color = {
    normal: 'var(--text-strong)',
    muted: 'var(--text-muted)',
    warning: 'var(--status-warning-fg)',
    danger: 'var(--status-danger-fg)',
  }[tone];

  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize,
        fontWeight: size === 'xl' || size === 'lg' ? 500 : 400,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {minor === null || minor === undefined ? '—' : formatMoney(minor, currency)}
    </span>
  );
}

/** Пояснение под цифрой: откуда она и когда обновлена. */
export function Provenance({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        font: '400 12px/1.45 var(--font-text)',
        color: 'var(--text-subtle)',
        display: 'block',
      }}
    >
      {children}
    </span>
  );
}

/** Надзаголовок с трекингом: единственное место, где допустимы прописные. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        font: '600 12px var(--font-text)',
        letterSpacing: 'var(--ls-caps)',
        textTransform: 'uppercase',
        color: 'var(--text-brand)',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Столбчатый график. Правило дизайн-системы: бары --rose-300, выделенный
 * --burgundy-700, без легенд и круговых диаграмм.
 */
export function BarChart({
  bars,
  currency,
  height = 160,
  emptyMessage = 'Расхода за этот период не было',
}: {
  bars: Array<{ label: string; value: number; caption?: string }>;
  currency: string;
  height?: number;
  emptyMessage?: string;
}) {
  const max = bars.reduce((peak, bar) => Math.max(peak, bar.value), 0);

  if (max <= 0) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          font: '400 13px var(--font-text)',
          color: 'var(--text-subtle)',
          background: 'var(--surface-sunken)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        {emptyMessage}
      </div>
    );
  }

  const lastWithValue = bars.reduce((index, bar, i) => (bar.value > 0 ? i : index), -1);

  return (
    <div className="kj-scroll-x">
      <div style={{ minWidth: bars.length * 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height }}>
          {bars.map((bar, index) => (
            <div
              key={bar.label}
              title={`${bar.caption ?? bar.label}: ${formatMoney(bar.value, currency)}`}
              style={{
                flex: 1,
                minWidth: 6,
                height: `${Math.max((bar.value / max) * 100, bar.value > 0 ? 2 : 0.5)}%`,
                background:
                  index === lastWithValue ? 'var(--burgundy-700)' : 'var(--rose-300)',
                borderRadius: '4px 4px 0 0',
                transition: 'height var(--dur-slow) var(--ease-standard)',
              }}
            />
          ))}
        </div>
        <div
          style={{
            marginTop: 'var(--space-3)',
            display: 'flex',
            justifyContent: 'space-between',
            font: '400 11px var(--font-mono)',
            color: 'var(--text-subtle)',
          }}
        >
          <span>{bars[0]?.label}</span>
          <span>{bars[bars.length - 1]?.label}</span>
        </div>
      </div>
    </div>
  );
}

/** Крупная цифра с надзаголовком — по образцу метрик из кита дизайн-системы. */
export function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div>
      <Eyebrow>{label}</Eyebrow>
      <div style={{ marginTop: 'var(--space-4)' }}>{value}</div>
      {hint ? <div style={{ marginTop: 'var(--space-3)' }}>{hint}</div> : null}
    </div>
  );
}

/** Курс: рублей за единицу валюты сервиса. */
export function Rate({ rate }: { rate: number | null }) {
  if (rate === null) return <span style={{ color: 'var(--text-subtle)' }}>—</span>;
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, whiteSpace: 'nowrap' }}>
      {formatNumber(rate, 2)} ₽
    </span>
  );
}
